import { useState } from "react";
import toast from "react-hot-toast";

import { patchSessionRouting } from "@/services/api/sessions";
import type { SubjectState } from "@/types/eryndal";
import { cn } from "@/utils/cn";

interface Props {
  subjectState: SubjectState;
  sessionId: string | null;
  forceClass: string;
  onForceClassChange: (value: string) => void;
  disabled?: boolean;
  onDropTopic: (topic: string) => void;
  onDropEntity: (entity: string) => void;
  onDropThread: (thread: string) => void;
}

interface ChipProps {
  label: string;
  disabled?: boolean;
  onDrop: () => void;
}

const ROUTE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Auto (classifier decides)" },
  { value: "lookup", label: "Lookup — fast path" },
  { value: "analysis", label: "Analysis — standard" },
  { value: "deep_work", label: "Deep Work — full ceremony" },
];

function Chip({ label, disabled, onDrop }: ChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-ruleline bg-parchment-50 px-2 py-0.5",
        "text-[0.7rem] text-espresso-700",
      )}
    >
      <span className="truncate max-w-[180px]">{label}</span>
      <button
        type="button"
        onClick={onDrop}
        disabled={disabled}
        aria-label={`Drop ${label}`}
        className={cn(
          "ml-0.5 text-warmgray-400 transition-colors hover:text-oxblood-500",
          disabled && "cursor-not-allowed opacity-40",
        )}
      >
        ×
      </button>
    </span>
  );
}

export function SubjectPanel({
  subjectState,
  sessionId,
  forceClass,
  onForceClassChange,
  disabled,
  onDropTopic,
  onDropEntity,
  onDropThread,
}: Props) {
  const { active_topics, primary_entities, open_threads, last_user_intent } = subjectState;
  const hasAnything =
    active_topics.length || primary_entities.length || open_threads.length || last_user_intent;
  const [remembering, setRemembering] = useState(false);

  const handleRememberForSession = async () => {
    if (!sessionId) return;
    setRemembering(true);
    try {
      await patchSessionRouting(sessionId, {
        force_class: forceClass || null,
      });
      toast.success("Routing preference saved for this session.");
    } catch {
      toast.error("Failed to save routing preference.");
    } finally {
      setRemembering(false);
    }
  };

  return (
    <div className="mb-4 px-3">
      {/* Routing override */}
      <div className="mb-3">
        <div className="pb-1 text-[0.65rem] uppercase tracking-[0.18em] text-warmgray-400">
          Routing
        </div>
        <select
          value={forceClass}
          onChange={(e) => onForceClassChange(e.target.value)}
          disabled={disabled}
          className={cn(
            "w-full text-xs border border-ruleline rounded-chip px-2 py-1 bg-parchment-50 text-espresso-700",
            "transition-colors duration-200 ease-atelier",
            "focus:outline-none focus:border-oxblood-300",
            disabled && "cursor-not-allowed opacity-40",
          )}
        >
          {ROUTE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {sessionId ? (
          <button
            type="button"
            onClick={handleRememberForSession}
            disabled={remembering || disabled}
            className={cn(
              "mt-1 text-[0.6rem] text-warmgray-400 hover:text-oxblood-500 transition-colors",
              (remembering || disabled) && "cursor-not-allowed opacity-40",
            )}
          >
            {remembering ? "Saving…" : "Remember for this session"}
          </button>
        ) : null}
      </div>

      {hasAnything ? (
        <>
          <div className="pb-2 text-[0.65rem] uppercase tracking-[0.18em] text-warmgray-400">
            Subjects
          </div>
          {active_topics.length ? (
            <div className="mb-2 flex flex-wrap gap-1">
              {active_topics.map((topic) => (
                <Chip
                  key={`topic-${topic}`}
                  label={topic}
                  disabled={disabled}
                  onDrop={() => onDropTopic(topic)}
                />
              ))}
            </div>
          ) : null}
          {open_threads.length ? (
            <div className="mb-2">
              <div className="mb-1 text-[0.6rem] uppercase tracking-[0.15em] text-warmgray-400">
                Open threads
              </div>
              <ul className="space-y-1">
                {open_threads.map((thread) => (
                  <li key={`thread-${thread}`} className="flex items-start gap-1 text-[0.7rem] text-espresso-700">
                    <span className="flex-1">{thread}</span>
                    <button
                      type="button"
                      onClick={() => onDropThread(thread)}
                      disabled={disabled}
                      aria-label={`Drop thread ${thread}`}
                      className={cn(
                        "text-warmgray-400 transition-colors hover:text-oxblood-500",
                        disabled && "cursor-not-allowed opacity-40",
                      )}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {primary_entities.length ? (
            <div className="mb-2 flex flex-wrap gap-1">
              {primary_entities.map((entity) => (
                <Chip
                  key={`entity-${entity}`}
                  label={entity}
                  disabled={disabled}
                  onDrop={() => onDropEntity(entity)}
                />
              ))}
            </div>
          ) : null}
          {last_user_intent ? (
            <p className="mt-1 text-[0.65rem] italic text-warmgray-400">
              {last_user_intent}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
